import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Row, Col, ListGroup, Image, Card, Button } from "react-bootstrap";
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Message from "../components/Message";
import Loader from "../components/Loader";
import { formatDate } from '../utils/dateUtils';
import {
  useDeliverOrderMutation,
  useGetOrderDetailsQuery,
  useGetPaypalClientIdQuery,
  usePayOrderMutation,
} from "../slices/ordersApiSlice";

const OrderScreen = () => {
  const { id: orderId } = useParams();

  const {
    data: order,
    refetch,
    isLoading,
    error,
  } = useGetOrderDetailsQuery(orderId);

  const [payOrder, { isLoading: loadingPay }] = usePayOrderMutation();

  const [deliverOrder, { isLoading: loadingDeliver }] =
    useDeliverOrderMutation();

  const { userInfo } = useSelector((state) => state.auth);

  const [{ isPending }, paypalDispatch] = usePayPalScriptReducer();

  const {
    data: paypal,
    isLoading: loadingPayPal,
    error: errorPayPal,
  } = useGetPaypalClientIdQuery();

  useEffect(() => {
    if (!errorPayPal && !loadingPayPal && paypal.clientId) {
      const loadPaypalScript = async () => {
        paypalDispatch({
          type: "resetOptions",
          value: {
            "client-id": paypal.clientId,
            currency: "ILS",
          },
        });
        paypalDispatch({ type: "setLoadingStatus", value: "pending" });
      };
      if (order && !order.isPaid) {
        if (!window.paypal) {
          loadPaypalScript();
        }
      }
    }
  }, [errorPayPal, loadingPayPal, order, paypal, paypalDispatch]);

  function onApprove(data, actions) {
    return actions.order.capture().then(async function (details) {
      try {
        await payOrder({ orderId, details }).unwrap();
        refetch();
        toast.success("ההזמנה שולמה בהצלחה");
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    });
  }

  // async function onApproveTest() {
  //   await payOrder({ orderId, details: { payer: {} } });
  //   refetch();

  //   toast.success("ההזמנה שולמה בהצלחה");
  // }

  function onError(err) {
    toast.error(err.message);
  }

  function createOrder(data, actions) {
    return actions.order
      .create({
        purchase_units: [
          {
            amount: { value: Math.round(order.totalPrice) },
          },
        ],
      })
      .then((orderID) => {
        return orderID;
      });
  }

  const deliverHandler = async () => {
    await deliverOrder(orderId);
    refetch();
  };

  return isLoading ? (
    <Loader />
  ) : error ? (
    <Message variant="danger">{error?.data?.message || error.error}</Message>
  ) : (
    <>
      <h1 className="text-end">{order._id} מספר הזמנה</h1>
      <Row>
        <Col md={4}>
          <Card>
            <ListGroup variant="flush">
              <ListGroup.Item>
                <h2 className="text-end">סיכום הזמנה</h2>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col className="text-end">₪{Math.round(order.itemsPrice)}</Col>
                  <Col className="text-end">פריטים</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col className="text-end">₪{Math.round(order.shippingPrice)}</Col>
                  <Col className="text-end">משלוח</Col>
                </Row>
              </ListGroup.Item>
              {/* <ListGroup.Item>
                <Row>
                  <Col className="text-end">₪{order.taxPrice}</Col>
                  <Col className="text-end">מס</Col>
                </Row>
              </ListGroup.Item> */}
              <ListGroup.Item>
                <Row>
                  <Col className="text-end">₪{Math.round(order.totalPrice)}</Col>
                  <Col className="text-end">סה"כ</Col>
                </Row>
              </ListGroup.Item>
              {!order.isPaid && (
                <ListGroup.Item>
                  {loadingPay && <Loader />}

                  {isPending ? (
                    <Loader />
                  ) : (
                    <div>
                      {/* <Button
                        style={{ marginBottom: '10px' }}
                        onClick={onApproveTest}
                      >
                        Test Pay Order
                      </Button> */}

                      <div>
                        <PayPalButtons
                          createOrder={createOrder}
                          onApprove={onApprove}
                          onError={onError}
                        ></PayPalButtons>
                      </div>
                    </div>
                  )}
                </ListGroup.Item>
              )}
              {/* {MARK AS DELIVERED} */}
              {loadingDeliver && <Loader />}

              {userInfo &&
                userInfo.isAdmin &&
                order.isPaid &&
                !order.isDelivered && (
                  <ListGroup.Item>
                    <Button
                      type="button"
                      className="btn btn-block"
                      onClick={deliverHandler}
                    >
                      סמן כנשלח
                    </Button>
                  </ListGroup.Item>
                )}
            </ListGroup>
          </Card>
        </Col>
        <Col md={8}>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2 className="text-end">משלוח</h2>
              <p className="text-end">
                <strong>שם: </strong>
                {order.user.name}
              </p>
              <p className="text-end">
                <a href={`mailto:${order.user.email}`}>{order.user.email}</a>
                {"     "}
                <strong>:מייל</strong>
              </p>
              <p className="text-end">
                <strong>כתובת:</strong>
                {"    "}
                {order.shippingAddress.address}, {order.shippingAddress.city}
                {"    "}
                {order.shippingAddress.postalCode},{"    "}
                {order.shippingAddress.country}
              </p>
              {order.isDelivered ? (
                <Message variant="success">
                  <div className="text-end">
                    {formatDate(order.deliveredAt)} נשלח בתאריך
                  </div>
                </Message>
              ) : (
                <Message variant="danger">
                  <div className="text-end">לא נשלח</div>
                </Message>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <h2 className="text-end">תשלום</h2>
              <p className="text-end">
                {order.paymentMethod}
                {"    "}
                <strong>:שיטת תשלום</strong>
              </p>
              {order.isPaid ? (
                <Message variant="success">
                  <div className="text-end">
                    {formatDate(order.paidAt)} שולם בתאריך
                  </div>
                </Message>
              ) : (
                <Message variant="danger">
                  <div className="text-end">לא שולם</div>
                </Message>
              )}
            </ListGroup.Item>

            <ListGroup.Item>
              <h2 className="text-end">פריטי הזמנה</h2>
              {order.orderItems.length === 0 ? (
                <Message>אין פריטים בהזמנה</Message>
              ) : (
                <ListGroup variant="flush">
                  {order.orderItems.map((item, index) => (
                    <ListGroup.Item key={index}>
                      <Row>
                        <Col md={4}>
                          {item.qty} x ₪{Math.round(item.price)} = ₪{Math.round(item.qty * item.price)}
                        </Col>
                        <Col>
                          <Link to={`/product/${item.product}`}>
                            {item.name}
                          </Link>
                          {item.customComment && (
                            <div className="text-end mt-1" style={{ fontSize: '0.85rem', color: '#555' }}>
                              <strong>הערה: </strong>{item.customComment}
                            </div>
                          )}
                        </Col>
                        <Col md={1}>
                          <Image
                            src={item.image}
                            alt={item.name}
                            fluid
                            rounded
                          />
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </ListGroup.Item>
          </ListGroup>
        </Col>
      </Row>
    </>
  );
};

export default OrderScreen;
